import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import Modal from 'components/Modal';
import { declineFlightTicketProposalOffer, useDeclineReasons } from 'api/Flights/sdk';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/Button';
import { t } from 'i18next';

const MAX_CHARACTERS = 2000;
const OPTION_DEFAULT_VALUE = '';

type DeclineOfferModalProps = {
  open: boolean;
  offerId: string;
  onClose: () => void;
};

const schema = yup.object().shape({
  reason: yup.string().required('Reason is required'),
  note: yup
    .string()
    .defined()
    .when(['reason'], {
      is: 'other',
      then: (schema) => schema.trim().min(4, t('flightOffer.otherReasonForDeclining')),
    }),
});

type DeclineOfferModalForm = yup.InferType<typeof schema>;

const DeclineOfferModal = ({ open, offerId, onClose }: DeclineOfferModalProps) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: declineReasons } = useDeclineReasons();

  const form = useForm({
    defaultValues: {
      reason: '',
      note: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const selectedReason = form.watch('reason');
  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleConfirm = (values: DeclineOfferModalForm) => {
    declineFlightTicketProposalOffer({
      flightTicketProposalId: offerId,
      declineReason: values.reason,
      declineNote: values.note,
    }).finally(handleClose);
  };

  return (
    <form onSubmit={form.handleSubmit(handleConfirm)} className='flex flex-col'>
      <Modal
        open={open}
        onClose={handleClose}
        title={t('flightOffer.declineOffer')}
        subtitle={t('flightOffer.reasonForDeclining')}
        content={
          <FormProvider {...form}>
            <Controller
              name='reason'
              render={({ field }) => (
                <select
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    form.trigger();
                  }}
                  aria-label='Decline Offer Select'
                  className={`border border-gray-400 rounded-sm p-2 mb-4 ${
                    selectedReason === OPTION_DEFAULT_VALUE ? 'text-gray-400' : ''
                  }`}
                >
                  <option value={OPTION_DEFAULT_VALUE} disabled>
                    {t('flightOffer.chooseReasonForDeclining')}
                  </option>

                  {declineReasons?.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              )}
            ></Controller>
            <div className='flex flex-col gap-1'>
              <label htmlFor='decline-note' className='font-[MuseoSansCyrl] text-sm text-gray-600'>
                {t('flightOffer.message')}
              </label>
              <Controller
                name='note'
                render={({ field }) => (
                  <textarea
                    {...field}
                    ref={textareaRef}
                    rows={4}
                    maxLength={MAX_CHARACTERS}
                    className={`border w-full rounded-sm ${form.formState.errors.note ? 'border-2 border-red' : 'border-gray-400'}`}
                  />
                )}
              ></Controller>
            </div>
            {form.formState.errors.note && (
              <p className='text-xs font-[MuseoSansCurl] text-red'>{form.formState.errors.note.message}</p>
            )}
            <p className='text-xs font-[MuseoSans900] text-gray-600 mt-2'>
              {t('flightOffer.charactersLeft')}: {MAX_CHARACTERS - form.watch('note').length}
            </p>
          </FormProvider>
        }
        buttons={
          <div className='w-full flex justify-end'>
            <Button type='submit' disabled={!form.formState.isValid}>
              {t('common.confirm')}
            </Button>
          </div>
        }
      />
    </form>
  );
};

export default DeclineOfferModal;
